import { ImagePaths } from '../../lib/imageUrls.js';

export const aiQuickStartExecutiveAssistants = {

    id: 60,
    slug: 'ai-quick-start-executive-assistants',
    ogImage: '/og/courses/ai-quick-start-executive-assistants.jpg',
    courseCode: 'AI-EA-QS-001',
    title: 'AI Quick Start for Executive Assistants',
    subtitle: 'One evening of practical AI for inbox, calendar, briefings, and travel',
    valueProposition: 'Use everyday AI tools to triage an executive inbox and calendar, build briefing packs, turn meeting notes into tracked actions, and draft in your executive voice without breaching confidentiality.',
    duration: '4 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_3,
    shortDescription: 'AI-EA-QS-001 — Practical AI for executive assistants covering inbox and calendar triage, briefing packs, meeting actions, and confidentiality boundaries.',
    price: {
      aud: 395,
      currency: 'AUD'
    },
    stripeConfig: {
    priceId: 'price_1U3WMtKu1nyc322U5Wk3y05Z',
    productId: 'prod_V3deez1kubSXT5'
  },

    // Drives the cohort registration panel on the course page. No `cohort`
    // is set until the next run is dated, so the form and the confirmation
    // email say "the next cohort".
    // Presence of this field is what renders the form — it is deliberately
    // separate from `promo` so retiring the discount does not silently
    // remove the waitlist as well.
    waitlist: {
      label: 'Next cohort'
    },

    snapshot: {
      whoItsFor: [
        'Executive assistants and personal assistants supporting one or more executives',
        'Board and committee secretaries preparing papers and minutes',
        'Office managers who combine executive support with operations',
        'Team and project administrators managing complex diaries and logistics',
        'Chiefs of staff and executive officers in small organisations'
      ],
      accreditation: 'Grounded in the Privacy Act 1988 and the Australian Privacy Principles, with confidentiality practice aligned to standard Australian corporate governance and record-keeping expectations.'
    },

    logistics: {
      classSize: 'Maximum 20 participants so everyone works on their own real scenarios',
      requirements: [
        'Computer with a working microphone and internet access (minimum 10 Mbps)',
        'Access to at least one general AI assistant such as ChatGPT, Copilot, Claude, or Gemini',
        'Two or three real examples of your own work with names and confidential detail removed',
        'Day-to-day responsibility for an executive diary, inbox, or meeting cycle',
        'No technical or prior AI experience required'
      ]
    },

    overview: 'Executive assistants absorb a volume and variety of work that almost no other role does: an inbox that never empties, a calendar with four competing claims on the same hour, briefing packs due before a flight, minutes that must become tracked actions, and correspondence that has to sound exactly like the executive rather than like you. This four-hour evening course shows how ordinary AI tools genuinely help with that specific workload. You will practise triaging and summarising a crowded inbox, resolving calendar conflicts with well-constructed options, assembling a one-page briefing pack from scattered source material, converting rough meeting notes into an owner-and-due-date action list, and drafting in a captured executive voice so the output needs editing rather than rewriting. Travel and event logistics get their own segment, because itineraries, agendas, run sheets, and contingency plans are exactly the structured drafting AI does well. The closing module deals with the part of this role that AI makes riskier: confidentiality. Board material, remuneration, personal contact details, health matters, commercial negotiations, and anything market-sensitive have clear boundaries under the Privacy Act 1988 and ordinary corporate obligations, and we cover how to keep the assistance while keeping the confidence. The price is 395 AUD including GST, in a single Wednesday evening session.',

    outcomes: [
      'Triage a crowded executive inbox into what needs a decision, a delegation, a reply, or nothing at all',
      'Resolve calendar conflicts by generating well-reasoned scheduling options with trade-offs stated',
      'Assemble a one-page executive briefing pack from scattered documents, emails, and notes',
      'Convert rough meeting notes into a clean action list with owners, due dates, and decisions recorded',
      'Draft correspondence in a captured executive voice so it needs light editing rather than a rewrite',
      'Build travel itineraries, event run sheets, and agendas with contingencies already considered',
      'Identify the confidential material that must never enter a general AI tool',
      'Sanitise a sensitive task so you keep the assistance and protect the executive and the organisation'
    ],

    audience: [
      'Executive assistants and personal assistants',
      'Board secretaries and committee support officers',
      'Office managers with executive support responsibilities',
      'Team administrators and project coordinators managing complex diaries',
      'Chiefs of staff and executive officers in small organisations',
      'Reception and administration staff moving into executive support roles'
    ],

    prerequisites: [
      'Day-to-day responsibility for an executive diary, inbox, meeting cycle, or travel arrangements',
      'Comfortable using email, calendar software, and a word processor',
      'Access to a general AI assistant, or willingness to create a free account before the session',
      'No technical, programming, or prior AI experience required',
      'Prompt Engineering Basics is helpful but not required'
    ],

    modules: [
      {
        title: 'Inbox and Calendar Triage',
        duration: '1 hour',
        topics: [
          'A five-part prompt structure applied to a real triage task, start to finish',
          'Sorting a crowded inbox into decisions, delegations, replies, and noise, with a daily summary format',
          'Drafting holding replies, escalations, and polite declines that protect the executive time',
          'Resolving calendar conflicts by generating options with the trade-offs stated rather than asking for a decision',
          'Preparing a daily and weekly diary brief the executive can read in ninety seconds'
        ]
      },
      {
        title: 'Briefing Packs and Meeting Actions',
        duration: '1 hour',
        topics: [
          'Building a one-page briefing pack from scattered emails, attachments, and prior papers',
          'Writing background, issue, options, and recommendation sections that survive executive scrutiny',
          'Turning rough meeting notes into a clean action register with owners, due dates, and decisions',
          'Drafting minutes and board or committee summaries at the right level of detail',
          'Fact-checking discipline: catching invented figures, dates, and attributions before the pack goes out'
        ]
      },
      {
        title: 'Travel, Events, and Drafting in an Executive Voice',
        duration: '1 hour',
        topics: [
          'Building travel itineraries with time zones, connections, buffers, and contingency options',
          'Producing event run sheets, agendas, and briefing notes for offsites and stakeholder visits',
          'Capturing an executive writing voice from past correspondence and reusing it as a reliable instruction',
          'Drafting sensitive correspondence such as apologies, declines, and follow-ups in that voice',
          'Knowing when to stop: the messages an executive must write personally, and why'
        ]
      },
      {
        title: 'Confidentiality Boundaries in an Executive Support Role',
        duration: '1 hour',
        topics: [
          'The red-line list: board and remuneration material, market-sensitive information, personal contact and health detail, legal advice, commercial negotiations, and anything under embargo',
          'Personal information and the Australian Privacy Principles under the Privacy Act 1988, in plain language',
          'What happens to text you paste into a consumer AI tool, and why free accounts are the highest-risk option',
          'Sanitisation patterns: anonymising participants, generalising figures, and splitting a task so the sensitive part never leaves your system',
          'Agreeing the boundary with your executive up front, and what to do when something has already been pasted'
        ]
      }
    ],

    format: {
      type: 'Live online evening session, Wednesday 6-10pm',
      includes: [
        '4 hours of live, instructor-led training in one evening',
        'Hands-on exercises using realistic executive support scenarios',
        'An executive assistant prompt library covering triage, briefings, minutes, and travel',
        'A briefing pack template and a meeting action register format',
        'A one-page confidentiality red-lines card for executive support work',
        'Certificate of completion suitable for continuing professional development records',
        'Live Q&A throughout, with a dedicated segment on your own scenarios'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Live online evening cohorts run on scheduled dates. Register your interest and we will confirm the next dates.'
    },

    // Real, dated sessions. The Enrol button is gated on there being a
    // future entry here — without one the page offers the waitlist instead,
    // because taking $395 for an unidentified session is not a supply
    // commitment under Australian Consumer Law. (#341)
    // Cohort opens the week of Monday 21 September 2026. NSW daylight saving
    // begins Sunday 4 October 2026 — see prompt-engineering-basics.js for why
    // the October run carries +11:00 rather than +10:00.
    sessions: [
      { start: '2026-09-23T18:00:00+10:00', end: '2026-09-23T22:00:00+10:00', capacity: 20 },
      { start: '2026-09-30T18:00:00+10:00', end: '2026-09-30T22:00:00+10:00', capacity: 20 },
      { start: '2026-10-07T18:00:00+11:00', end: '2026-10-07T22:00:00+11:00', capacity: 20 }
    ],

    pathways: [],

    skillsTaught: [
      'Inbox and calendar triage with AI',
      'Executive briefing pack preparation',
      'Meeting notes to tracked actions',
      'Drafting in an executive voice',
      'Confidentiality boundaries for AI use'
    ],

    hasCapstone: false,

    nextCourses: [
      'ai-for-business'
    ],

    faqs: [
      {
        question: 'Will AI have access to my executive inbox or calendar?',
        answer: 'No. Nothing in this course connects an AI tool to a live mailbox or diary, and we do not ask you to grant any permissions. You work by pasting sanitised text into an assistant, which keeps you in complete control of what is shared. If your organisation has already deployed an integrated assistant such as Microsoft 365 Copilot, everything taught here still applies within it.'
      },
      {
        question: 'Do I need a Microsoft 365 Copilot licence?',
        answer: 'No. The course is deliberately tool-agnostic and works with whichever general assistant your organisation has approved, including free tiers. We demonstrate on the widely used tools so you can transfer the techniques wherever you land.'
      },
      {
        question: 'How do I capture my executive writing voice without sharing confidential emails?',
        answer: 'You do not need confidential material to do it. The technique uses two or three pieces of past correspondence with names, figures, and specifics stripped out, from which you extract a reusable description of tone, sentence length, formality, and habits. We build one live in the session and you leave with your own.'
      },
      {
        question: 'What must never go into a general AI tool in this role?',
        answer: 'Board and remuneration material, market-sensitive or price-sensitive information, legal advice, live commercial negotiations, personal contact and health details, and anything under embargo. The confidentiality module works through each with real examples, gives you a one-page card, and shows sanitisation patterns so you can still get help with the surrounding work.'
      },
      {
        question: 'What if I have already pasted something I should not have?',
        answer: 'It happens, and the course covers it directly rather than pretending it does not. We walk through the practical response: what to check in the tool settings, what to delete, who to tell internally, and when it becomes a notifiable matter under the Notifiable Data Breaches scheme. Knowing the steps in advance is what keeps a mistake small.'
      },
      {
        question: 'Can AI take minutes for me?',
        answer: 'AI is good at turning your rough notes into structured minutes and a clean action register, which is what this course teaches. Live automated transcription of a meeting is a separate matter with consent obligations under Australian recording laws that vary by state, and it needs an organisational decision rather than an individual one. We flag the boundary so you know when to escalate the question.'
      },
      {
        question: 'Can several assistants from one organisation attend together?',
        answer: 'Yes, and it works well because the group leaves with a shared prompt library and a consistent view of the confidentiality boundary. For five or more participants from the same organisation, contact us for a private quote. Private cohorts using your own document types are also arranged on request.'
      },
      {
        question: 'Is the price inclusive of GST?',
        answer: 'Yes. The price is 395 AUD including GST per person, which is the full amount payable. A tax invoice is issued automatically on enrolment.'
      }
    ]
};
